import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ReactFlow, ReactFlowProvider, Background, Controls, Handle, Position,
  MarkerType, useNodesState, useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Box, Card, IconButton, Portal, Slider, Stack,
  Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import BoltIcon from '@mui/icons-material/Bolt';
import { DiscoveryIcon } from '../global/CustomIcons';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

import { fetchRequest as fetchGraph } from '../../redux/actions/discovery/graph';
import EdgeDrawer from './EdgeDrawer';
import { useDisplayNames } from '../../utils/displayNames';

/* ── Geometry ────────────────────────────────────────────────────────── */

const COL_GAP = 250;
const ROW_GAP = 98;
const NODE_R = { goal: 44, driver: 32, dim: 18 };

const LIFECYCLE_STYLE = {
  validated: { label: 'Validated' },
  revalidated: { label: 'Validated' },
  candidate: { label: 'Candidate', dashed: true },
  exploratory: { label: 'Suggestive', dashed: true, exploratory: true },
  weakening: { label: 'Weakening', weak: true },
  broken: { label: 'Broken', dead: true },
};

/* Strength → colour. Every live relationship sits on one continuous ramp so
   the eye reads strength directly: weak runs warm/red, mid runs amber, strong
   runs green. Validation is carried by line STYLE (solid vs dashed), not hue,
   so the two dimensions never collide. */
const scoreColor = (theme, score) => {
  const s = Math.max(0, Math.min(1, score || 0));
  // hue ramp: 6° (red) → 45° (amber) → 132° (green)
  const hue = s < 0.5
    ? 6 + (45 - 6) * (s / 0.5)
    : 45 + (132 - 45) * ((s - 0.5) / 0.5);
  const light = theme.palette.mode === 'dark' ? 60 : 44;
  return `hsl(${Math.round(hue)}, 74%, ${light}%)`;
};

/* Thickness by impact: the more the model leans on a signal, the bolder. */
const scoreWidth = (score) => 1.3 + 5.4 * Math.max(0, Math.min(1, score || 0));

/* Opacity rides confidence (mu): low-confidence candidates fade right back,
   high-confidence drivers light up fully, so the eye reads how much the model
   believes each signal at a glance. */
const confidenceOpacity = (mu) => 0.16 + 0.82 * Math.max(0, Math.min(1, mu || 0));

/* One unified graph — no causal/predictive toggle. Both questions are read
   off the same picture through independent visual channels:
   - line STYLE = trust:    solid = validated (causal), dashed = suggestive /
     candidate (predictive-only, not a causal claim).
   - hue + opacity + glow = CONFIDENCE (mu): how much the model believes it.
   - thickness = IMPACT:    how hard the model leans on it.
   The strength slider filters by confidence, so dragging RIGHT peels the graph
   back from "everything the model uses" to just the validated causal core. */
const edgeConfidence = (e) => (e?.mu ?? 0);

/* Lifecycles that count as a proven causal relationship (solid line, lights a
   node as a validated driver). Everything else (suggestive / candidate) is the
   predictive-only tier: dashed, shown but never a causal claim. */
const CLEARED_LIFECYCLES = new Set(
  ['validated', 'revalidated', 'weakening', 'broken']);

/* Humanize bins at the goal cadence: 2 bins @ 1d -> "2d". */
const fmtBins = (bins, interval) => {
  if (bins == null || !interval) return null;
  const total = bins * interval;
  if (interval >= 86400) return `${Math.round(total / 86400)}d`;
  if (interval >= 3600) return `${Math.round(total / 3600)}h`;
  if (interval >= 60) return `${Math.round(total / 60)}m`;
  return `${total}s`;
};

const cssId = (id) => (window.CSS && window.CSS.escape ? window.CSS.escape(id) : id);

/* Strength knob = a pure filter. By default we show everything with any real
   signal (score ≥ 0.1); each edge already encodes its own strength via colour,
   opacity and thickness, so the resting view is a full strength map. Sliding
   RIGHT hides the weaker relationships, leaving only the strong ones. */
const DEFAULT_MIN_SCORE = 0.1;
// Confidence (mu) bands the discovery layer assigns: candidate 0.10–0.35,
// suggestive 0.40–0.70, validated 0.80–1.00. The marks sit on the band
// boundaries so each detent peels off exactly one tier.
const SCORE_MARKS = [
  { value: 0.1, label: 'all' },
  { value: 0.4, label: 'suggestive' },
  { value: 0.8, label: 'validated' },
];

/* ── The pulse glyph (the reference's waveform mark) ─────────────────── */

const Pulse = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path
      d="M4 16h6l3-8 5 16 3-8h7"
      stroke={color}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ── Nodes: glowing circles, label beneath ───────────────────────────── */

const CircleNode = ({ data }) => {
  const theme = useTheme();
  const accent = data.accent;
  const r = data.radius;
  const expl = data.exploratory;
  const glow = Math.max(0, Math.min(1, data.glow ?? 0));

  return (
    <Box sx={{
      width: r * 2,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      // Suggestive nodes sit between lit drivers and the dim constellation.
      opacity: data.related ? (expl ? 0.82 : 1) : 0.35,
      cursor: data.related && data.kind !== 'goal' ? 'pointer' : 'default',
    }}>
      <Box sx={{
        width: r * 2,
        height: r * 2,
        borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: `${data.kind === 'goal' ? 2 : 1.5}px ${expl ? 'dashed' : 'solid'} ${alpha(accent, data.related ? 0.9 : 0.5)}`,
        backgroundColor: alpha(theme.palette.background.default, 0.92),
        // Glow grows with the node's confidence/strength: faint for weak
        // candidates, a bright halo for high-confidence drivers.
        boxShadow: data.related && !expl
          ? `0 0 ${r / 2 + glow * r * 0.7}px ${alpha(accent, 0.22 + glow * 0.5)}, inset 0 0 ${r / 2.5}px ${alpha(accent, 0.14 + glow * 0.22)}`
          : (expl ? `0 0 ${r / 3}px ${alpha(accent, 0.25)}` : 'none'),
      }}>
        {data.kind === 'event'
          ? <BoltIcon sx={{ fontSize: r * 0.9, color: accent }} />
          : <Pulse size={r * 1.15} color={accent} />}
      </Box>
      <Typography
        noWrap
        sx={{
          mt: 0.75,
          maxWidth: 150,
          px: 0.75, py: 0.1,
          fontSize: data.kind === 'goal' ? 12 : 11,
          fontWeight: data.kind === 'goal' ? 700 : 500,
          fontFamily: 'monospace',
          color: data.related ? 'text.primary' : 'text.disabled',
          backgroundColor: alpha(theme.palette.background.paper, 0.85),
          border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          borderRadius: 0.75,
        }}
      >
        {data.label}
      </Typography>
      <Handle type="target" position={Position.Left}
        style={{ opacity: 0, top: r, left: 2 }} />
      <Handle type="source" position={Position.Right}
        style={{ opacity: 0, top: r, right: 2 }} />
    </Box>
  );
};

const nodeTypes = { circle: CircleNode };

/* Re-fit the viewport whenever the knob changes the visible set — newly
   woken drivers reshuffle the layout, so the camera follows. */
const Refit = ({ signal }) => {
  const { fitView } = useReactFlow();
  useEffect(() => {
    // Debounce so a slider DRAG (many rapid signal changes) coalesces into a
    // single re-fit instead of stacking animations, and run the fit on the
    // next animation frame so its layout read never re-enters the same frame
    // that scheduled it — that reentrancy is what makes ResizeObserver emit
    // "loop completed with undelivered notifications" and trip the dev overlay.
    let raf = 0;
    const t = setTimeout(() => {
      raf = requestAnimationFrame(
        () => fitView({ padding: 0.08, maxZoom: 1.2, duration: 350 }));
    }, 120);
    return () => { clearTimeout(t); if (raf) cancelAnimationFrame(raf); };
  }, [signal, fitView]);
  return null;
};

/* ── Layout ──────────────────────────────────────────────────────────── */
/* Related nodes flow left→right by depth (sources → mediators → goal).
   The rest of the workspace universe rings the cluster as a dimmed
   constellation — everything was screened; only these earned an edge.  */

const layoutGraph = (graph) => {
  const apiNodes = graph?.nodes || [];
  const apiEdges = graph?.edges || [];

  const related = apiNodes.filter((n) => n.related);
  const dim = apiNodes.filter((n) => !n.related);
  const goal = related.find((n) => n.kind === 'goal');

  // Outgoing adjacency: where does each node's influence flow?
  const outs = {};
  apiEdges.forEach((e) => {
    const a = e?.source?.key;
    const b = e.mediator_key || e.target_key;
    if (a && b) (outs[a] = outs[a] || []).push(b);
  });

  // Column = longest influence path to the goal (goal 0, direct drivers 1,
  // drivers-of-drivers 2, ...). Derived from the edges themselves, so a
  // mediator like btc_close lands between its sources and the goal even
  // though every edge is stored as depth 1.
  const layers = {};
  const layerOf = (id, trail) => {
    if (goal && id === goal.id) return 0;
    if (layers[id] != null) return layers[id];
    if (trail.has(id)) return 1; // cycle guard
    trail.add(id);
    const ts = outs[id] || [];
    const l = ts.length ? 1 + Math.max(...ts.map((t) => layerOf(t, trail))) : 1;
    trail.delete(id);
    layers[id] = l;
    return l;
  };
  related.forEach((n) => { layers[n.id] = layerOf(n.id, new Set()); });
  const maxLayer = Math.max(1, ...related.map((n) => layers[n.id]));

  // Lay layers out right→left (goal at x=0, direct drivers just left of it,
  // drivers-of-drivers further left). A layer with many nodes WRAPS into
  // several sub-columns rather than one tall stack, so the cluster stays
  // roughly square and the nodes never shrink to dots — the common case here
  // is a goal with dozens of direct (depth-1) drivers, which previously piled
  // into a single thin vertical column. Within each sub-column, rows are
  // ordered by the barycenter of each node's targets so edges flow cleanly.
  const MAX_ROWS = 7;                  // a layer taller than this wraps wider
  const SUBCOL_GAP = COL_GAP * 0.72;   // horizontal gap between wrapped columns
  const positions = {};
  if (goal) positions[goal.id] = { x: 0, y: 0 };
  let baseX = 0;                       // right edge of the next layer's block
  for (let L = 1; L <= maxLayer; L += 1) {
    const group = related.filter((n) => n.kind !== 'goal' && layers[n.id] === L);
    if (!group.length) continue;
    const bary = (n) => {
      const ys = (outs[n.id] || [])
        .map((t) => positions[t]?.y)
        .filter((y) => y != null);
      return ys.length ? ys.reduce((s, y) => s + y, 0) / ys.length : 0;
    };
    const sorted = [...group].sort((a, b) => bary(a) - bary(b));
    const m = sorted.length;
    const cols = Math.ceil(m / MAX_ROWS);
    const rows = Math.ceil(m / cols);        // rebalance so columns are even
    const totalH = (rows - 1) * ROW_GAP;
    const rightX = baseX - COL_GAP;          // sub-column nearest the goal
    sorted.forEach((n, i) => {
      const col = Math.floor(i / rows);      // 0 = closest to the goal
      const row = i % rows;
      const odd = col % 2 === 1;             // half-row stagger between columns
      positions[n.id] = {
        x: rightX - col * SUBCOL_GAP,
        y: row * ROW_GAP - totalH / 2 + (odd ? ROW_GAP * 0.5 : 0),
      };
    });
    baseX = rightX - (cols - 1) * SUBCOL_GAP;  // next (deeper) layer goes left
  }

  // Constellation ring for the unrelated universe. The ellipse is wide and
  // flat to mirror the canvas aspect, so screened signals fill the side
  // space instead of stretching the view vertically.
  const xs = Object.values(positions).map((p) => p.x);
  const ys = Object.values(positions).map((p) => p.y);
  const cx = xs.length ? (Math.min(...xs) + Math.max(...xs)) / 2 : 0;
  const cy = ys.length ? (Math.min(...ys) + Math.max(...ys)) / 2 : 0;
  const rx = Math.max(((Math.max(...xs, 0) - Math.min(...xs, 0)) / 2) + 430, 600);
  const ry = Math.max(((Math.max(...ys, 0) - Math.min(...ys, 0)) / 2) + 40, 160);
  dim.forEach((n, i) => {
    const angle = (i / Math.max(1, dim.length)) * Math.PI * 2 + 0.4;
    positions[n.id] = {
      x: cx + rx * Math.cos(angle) * (0.9 + ((i * 7919) % 11) / 50),
      y: cy + ry * Math.sin(angle),
    };
  });

  return { related, dim, positions };
};

/* ── Flow assembly (hover handled separately, in CSS) ────────────────── */

const buildNodes = (graph, theme, nameOf = (k) => k) => {
  if (!graph) return [];
  const { related, dim, positions } = layoutGraph(graph);

  // A node takes the colour of its highest-confidence incident relationship,
  // so the gradient reads end-to-end: high-confidence drivers glow green,
  // low-confidence candidates sit warm/dim.
  const scoreByNode = {};
  (graph.edges || []).forEach((e) => {
    const sc = edgeConfidence(e);
    [e?.source?.key, e.mediator_key || e.target_key].forEach((id) => {
      if (id) scoreByNode[id] = Math.max(scoreByNode[id] ?? 0, sc);
    });
  });

  const accentFor = (n) => {
    if (n.kind === 'goal') return theme.palette.primary.main;
    if (!n.related) return theme.palette.text.disabled;
    return scoreColor(theme, scoreByNode[n.id] ?? 0);
  };
  // Suggestive nodes are real drivers-in-waiting: full driver size, just a
  // different hue, so the slider reveal reads as "more relationships," not
  // "smaller dots."
  const radiusFor = (n) => (
    n.kind === 'goal' ? NODE_R.goal : (n.related ? NODE_R.driver : NODE_R.dim));

  return [...related, ...dim].map((n) => ({
    id: n.id,
    type: 'circle',
    position: {
      x: positions[n.id].x - radiusFor(n),
      y: positions[n.id].y - radiusFor(n),
    },
    data: {
      label: n.label || nameOf(n.id),
      kind: n.kind,
      related: Boolean(n.related),
      exploratory: Boolean(n.exploratory),
      radius: radiusFor(n),
      accent: accentFor(n),
      // Confidence/strength of the node's strongest relationship (0..1) so the
      // glow intensity reads belief directly — brightest = most lit-up.
      glow: n.kind === 'goal' ? 1 : (scoreByNode[n.id] ?? 0),
    },
    draggable: true,
  }));
};

const buildEdges = (graph, theme) => {
  if (!graph) return [];
  const interval = graph.interval_seconds;
  const horizon = graph.horizon_bins;

  return (graph.edges || []).map((e) => {
    const style = LIFECYCLE_STYLE[e.lifecycle] || LIFECYCLE_STYLE.candidate;
    const mu = e.mu ?? 0;
    const impact = e.impact ?? 0;

    // Confidence (mu) drives hue + opacity; impact drives thickness. Health
    // states (broken/weakening) override the hue since they're about decay
    // over time, not confidence.
    const color = style.dead
      ? theme.palette.text.disabled
      : style.weak
        ? theme.palette.warning.main
        : scoreColor(theme, mu);
    const strokeWidth = style.dead ? 1.4 : scoreWidth(impact);
    const strokeOpacity = style.dead ? 0.5 : confidenceOpacity(mu);

    const lag = fmtBins(e.lag_bins, interval);
    const hor = fmtBins(horizon, interval);
    const sign = e.direction === 'negative' ? '− ' : '';
    const label = lag != null && hor != null
      ? `${sign}${lag} lag · ${hor} horizon` : undefined;

    return {
      id: e.edge_id,
      source: e?.source?.key,
      // depth-1 edges land on the goal; deeper hops land on their mediator.
      target: e.mediator_key || e.target_key,
      type: 'default',
      animated: e.lifecycle === 'candidate',
      label,
      labelStyle: {
        fill: theme.palette.text.secondary,
        fontSize: 10,
        fontFamily: 'monospace',
      },
      labelBgStyle: {
        fill: theme.palette.background.paper,
        fillOpacity: 0.92,
        stroke: alpha(theme.palette.divider, 0.8),
      },
      labelBgPadding: [6, 3],
      labelBgBorderRadius: 6,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 16,
        height: 16,
        markerUnits: 'userSpaceOnUse',
        color: alpha(color, Math.min(1, strokeOpacity + 0.1)),
      },
      style: {
        stroke: alpha(color, strokeOpacity),
        strokeWidth,
        // Solid = validated/proven; dashed = unvalidated (suggestive/candidate).
        strokeDasharray: style.dashed ? '7 5' : undefined,
      },
      data: e,
    };
  });
};

/* ── Component ───────────────────────────────────────────────────────── */

const RelationshipGraph = ({ goalId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const nameOf = useDisplayNames();
  // The redux store holds the LAST goal's graph until the new fetch resolves;
  // rendering it would flash the previous goal's map. Only trust the response
  // once its goal_id matches the one we're viewing, and treat the mismatch
  // window as loading so we never paint stale nodes.
  const rawGraph = useSelector((s) => s.discovery_graph?.response);
  const fetching = useSelector((s) => Boolean(s.discovery_graph?.isLoading));
  const graphMatchesGoal = Boolean(rawGraph) && rawGraph.goal_id === goalId;
  const graph = graphMatchesGoal ? rawGraph : null;
  const isLoading = fetching || (Boolean(goalId) && !graphMatchesGoal);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [minScore, setMinScore] = useState(DEFAULT_MIN_SCORE);
  const [fullscreen, setFullscreen] = useState(false);

  // Esc exits fullscreen, matching the platform convention.
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  useEffect(() => {
    if (goalId) dispatch(fetchGraph({ goalId }));
  }, [goalId, dispatch]);

  // Reset the knob to "show everything with signal" whenever a new goal's
  // graph loads — strength is read from each edge's own colour/weight, so the
  // full map is the right resting state.
  useEffect(() => {
    if (graph) setMinScore(DEFAULT_MIN_SCORE);
  }, [graph]);

  // The knob filters edges by CONFIDENCE (mu); a node lights up when it
  // touches a visible edge, so low-confidence candidates fade out as the
  // threshold rises (drag right → validated-only). Nodes lit ONLY by
  // unvalidated edges (suggestive/candidate) carry the dashed-border hint.
  const viewGraph = useMemo(() => {
    if (!graph) return graph;
    const visible = (graph.edges || []).filter(
      (e) => edgeConfidence(e) >= minScore);
    const litValidated = new Set([graph.target_key]);
    const litUnvalidated = new Set();
    visible.forEach((e) => {
      const a = e?.source?.key;
      const b = e.mediator_key || e.target_key;
      const bucket = CLEARED_LIFECYCLES.has(e.lifecycle)
        ? litValidated : litUnvalidated;
      if (a) bucket.add(a);
      bucket.add(b);
    });
    return {
      ...graph,
      edges: visible,
      nodes: (graph.nodes || []).map((n) => ({
        ...n,
        related: n.kind === 'goal'
          || litValidated.has(n.id) || litUnvalidated.has(n.id),
        exploratory: n.kind !== 'goal'
          && litUnvalidated.has(n.id) && !litValidated.has(n.id),
      })),
    };
  }, [graph, minScore]);

  // Nodes live in ReactFlow state so select/drag changes apply natively;
  // they're rebuilt only when the graph payload (or theme) changes.
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  useEffect(() => {
    setNodes(buildNodes(viewGraph, theme, nameOf));
  }, [viewGraph, theme, setNodes, nameOf]);

  const edges = useMemo(
    () => buildEdges(viewGraph, theme), [viewGraph, theme]);

  // Hover lighting is pure CSS over stable elements — no React re-render
  // of nodes/edges, so nothing flickers.
  const hoverCss = useMemo(() => {
    if (!hoveredId) return '';
    const litNodes = new Set([hoveredId]);
    const litEdges = [];
    (viewGraph?.edges || []).forEach((e) => {
      const a = e?.source?.key;
      const b = e.mediator_key || e.target_key;
      if (a === hoveredId || b === hoveredId) {
        litEdges.push(e.edge_id);
        litNodes.add(a);
        litNodes.add(b);
      }
    });
    const nodeSel = [...litNodes].map(
      (id) => `.rg-flow .react-flow__node[data-id="${cssId(id)}"]`).join(',\n');
    const edgeSel = litEdges.map(
      (id) => `.rg-flow .react-flow__edge[data-id="${cssId(id)}"]`).join(',\n');
    const labelSel = litEdges.map(
      (id) => `.rg-flow .react-flow__edge[data-id="${cssId(id)}"] .react-flow__edge-textwrapper`).join(',\n');
    return `
      .rg-flow .react-flow__node { opacity: 0.15; }
      .rg-flow .react-flow__edge { opacity: 0.1; }
      ${nodeSel} { opacity: 1; }
      ${edgeSel ? `${edgeSel} { opacity: 1; }` : ''}
      ${labelSel ? `${labelSel} { opacity: 1; }` : ''}
    `;
  }, [hoveredId, viewGraph]);

  const driverCount = (viewGraph?.nodes || []).filter(
    (n) => n.related && !n.exploratory && n.kind !== 'goal').length;
  const suggestiveCount = (viewGraph?.nodes || []).filter(
    (n) => n.exploratory).length;

  // The threshold can be dragged past the strongest relationship, which hides
  // every edge and leaves only the dim constellation — a state that reads as
  // "the graph broke." When that happens we keep the full slider range but
  // surface an explicit, self-explaining empty state over the canvas.
  const totalEdges = (graph?.edges || []).length;
  const visibleEdges = (viewGraph?.edges || []).length;
  const noVisibleEdges = !isLoading && totalEdges > 0 && visibleEdges === 0;

  // A node click opens its strongest edge — the circle IS the relationship.
  const onNodeClick = (_e, node) => {
    if (!node?.data?.related || node.data.kind === 'goal') return;
    const best = (viewGraph?.edges || [])
      .filter((e) => e?.source?.key === node.id)
      .sort((a, b) => Math.abs(b?.strength?.lift ?? 0) - Math.abs(a?.strength?.lift ?? 0))[0];
    if (best) setSelectedEdgeId(best.edge_id);
  };

  // Empty card only when the universe itself is empty — with signals present
  // we render the dim constellation even before any driver validates.
  if (!isLoading && (graph?.nodes || []).length <= 1) {
    return (
      <Card variant="outlined">
        <Stack alignItems="center" spacing={1.5} sx={{ py: 6 }}>
          <DiscoveryIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
          <Typography variant="subtitle2">No relationships yet</Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 380 }}>
            The graph fills in as discovery validates drivers. If a run is in
            progress, check the Run tab — candidates appear after the prune stage.
          </Typography>
        </Stack>
      </Card>
    );
  }

  const card = (
    <Card
      variant="outlined"
      sx={fullscreen
        ? {
          // Sit above the app shell (sidebar/app bar) — the card is portaled
          // to <body> so `fixed` covers the true viewport, not the content
          // region trapped inside a transformed layout ancestor.
          position: 'fixed', inset: 0, zIndex: theme.zIndex.modal + 10,
          m: 0, borderRadius: 0,
        }
        : { position: 'relative' }}
    >
      {hoverCss && <style>{hoverCss}</style>}
      <Box
        className="rg-flow"
        sx={{
          height: fullscreen ? '100vh' : 560,
          backgroundColor: alpha(theme.palette.background.default, 0.6),
          '& .react-flow__node, & .react-flow__edge': {
            transition: 'opacity 150ms ease',
          },
          // Lag/horizon pills appear on hover (per-edge, via hoverCss) —
          // the resting view stays clean curves.
          '& .react-flow__edge-textwrapper': {
            opacity: 0,
            transition: 'opacity 150ms ease',
          },
          '& .react-flow__controls': {
            boxShadow: 'none',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
          },
          '& .react-flow__controls-button': {
            backgroundColor: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`,
            '& svg': { fill: theme.palette.text.secondary },
            '&:hover': { backgroundColor: theme.palette.action.hover },
          },
        }}
      >
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            fitView
            fitViewOptions={{ padding: 0.08, maxZoom: 1.2 }}
            minZoom={0.25}
            proOptions={{ hideAttribution: true }}
            onEdgeClick={(_e, edge) => setSelectedEdgeId(edge?.data?.edge_id || null)}
            onNodeClick={onNodeClick}
            onNodeMouseEnter={(_e, node) => setHoveredId(node.id)}
            onNodeMouseLeave={() => setHoveredId(null)}
            nodesConnectable={false}
            elementsSelectable
          >
            <Background gap={26} size={1.2} color={alpha(theme.palette.text.primary, 0.07)} />
            <Controls showInteractive={false} />
            <Refit signal={`${minScore}-${fullscreen}`} />
          </ReactFlow>
        </ReactFlowProvider>

        {noVisibleEdges && (
          <Stack
            alignItems="center"
            spacing={1}
            sx={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              px: 3, py: 2, borderRadius: 1.5,
              textAlign: 'center', maxWidth: 320,
              pointerEvents: 'none',
              border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
              backgroundColor: alpha(theme.palette.background.paper, 0.92),
              backdropFilter: 'blur(2px)',
              boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.18)}`,
            }}
          >
            <DiscoveryIcon sx={{ fontSize: 30, color: 'text.disabled' }} />
            <Typography variant="subtitle2">
              No relationships ≥ {minScore.toFixed(2)} strength
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Drag the strength knob left to reveal weaker relationships.
            </Typography>
          </Stack>
        )}
      </Box>

      {/* Header chrome */}
      <Stack
        direction="row" spacing={1.5} alignItems="center"
        sx={{ position: 'absolute', top: 10, left: 12 }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {driverCount} driver{driverCount === 1 ? '' : 's'} lit
          {suggestiveCount > 0 && ` · ${suggestiveCount} suggestive`}
          {' · '}{(graph?.nodes || []).length - 1} screened
        </Typography>
      </Stack>

      {/* Legend: the strength ramp (red→green) plus what line STYLE means.
          Strength is the hue/weight; validation is solid vs dashed. */}
      <Stack
        spacing={0.75}
        sx={{
          position: 'absolute', bottom: 10, left: 12,
          px: 1.5, py: 0.85, borderRadius: 1,
          border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
          backgroundColor: alpha(theme.palette.background.paper, 0.88),
        }}
      >
        <Stack direction="row" spacing={0.85} alignItems="center">
          <Typography variant="caption" sx={{ fontSize: 10 }} color="text.secondary">
            low confidence
          </Typography>
          <Box sx={{
            width: 96, height: 6, borderRadius: 3,
            background: `linear-gradient(90deg, ${scoreColor(theme, 0.1)}, ${scoreColor(theme, 0.5)}, ${scoreColor(theme, 0.95)})`,
          }} />
          <Typography variant="caption" sx={{ fontSize: 10 }} color="text.secondary">
            high confidence
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1.75} alignItems="center">
          <Stack direction="row" spacing={0.6} alignItems="center">
            <Box sx={{ width: 16, height: 0, borderTop: `2px solid ${theme.palette.text.secondary}` }} />
            <Typography variant="caption" sx={{ fontSize: 10 }} color="text.secondary">
              proven
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.6} alignItems="center">
            <Box sx={{ width: 16, height: 0, borderTop: `2px dashed ${theme.palette.text.secondary}` }} />
            <Tooltip
              arrow
              title="Dashed = suggestive: lift-tested relationships that fell short of the validation bar. Shown for exploration, never used in forecasts."
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: 10, cursor: 'help',
                  textDecoration: 'underline dotted',
                  textUnderlineOffset: 2,
                }}
                color="text.secondary"
              >
                suggestive
              </Typography>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>
      {/* Strength knob: a filter — drag RIGHT to hide weaker relationships.
          Centered at the top so it reads as the graph's primary control. */}
      <Stack
        spacing={0}
        sx={{
          position: 'absolute', top: 26, left: '50%',
          transform: 'translateX(-50%)',
          width: 360, maxWidth: '82%', px: 1.75, pt: 0.3, pb: 0.6,
          borderRadius: 1.25,
          border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
          backgroundColor: alpha(theme.palette.background.paper, 0.92),
          backdropFilter: 'blur(2px)',
          boxShadow: `0 1px 8px ${alpha(theme.palette.common.black, 0.16)}`,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          align="center"
          sx={{ fontSize: 10, fontWeight: 600, lineHeight: 1.25 }}
        >
          Show relationships ≥ {minScore.toFixed(2)} confidence
        </Typography>
        <Slider
          value={minScore}
          onChange={(_e, v) => setMinScore(Array.isArray(v) ? v[0] : v)}
          min={0.1}
          max={0.95}
          step={0.05}
          marks={SCORE_MARKS}
          sx={{
            py: 0.25,
            height: 4,
            '& .MuiSlider-rail': { opacity: 0.35 },
            // Compact, high-contrast tick marks so the band boundaries read as
            // clear separators rather than faint dots.
            '& .MuiSlider-mark': {
              width: 2,
              height: 10,
              borderRadius: 0,
              backgroundColor: alpha(theme.palette.text.primary, 0.55),
              transform: 'translate(-50%, -50%)',
            },
            '& .MuiSlider-markActive': {
              backgroundColor: alpha(theme.palette.background.paper, 0.9),
              opacity: 1,
            },
            '& .MuiSlider-markLabel': {
              fontSize: 9.5,
              top: 13,
              color: 'text.secondary',
              whiteSpace: 'nowrap',
            },
            // Keep the outer labels inside the box instead of bleeding out.
            '& .MuiSlider-markLabel[data-index="0"]': {
              transform: 'translateX(0)',
            },
            '& .MuiSlider-markLabel[data-index="2"]': {
              transform: 'translateX(-100%)',
            },
            '& .MuiSlider-thumb': {
              width: 14, height: 14,
              boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}`,
            },
          }}
        />
      </Stack>
      <Stack
        direction="row" spacing={0.5}
        sx={{ position: 'absolute', top: 8, right: 8 }}
      >
        <Tooltip title="Refresh graph" arrow>
          <IconButton
            size="small"
            onClick={() => dispatch(fetchGraph({ goalId }))}
            sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.7) }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={fullscreen ? 'Exit full screen (Esc)' : 'Full screen'} arrow>
          <IconButton
            size="small"
            onClick={() => setFullscreen((v) => !v)}
            sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.7) }}
          >
            {fullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>

      <EdgeDrawer
        edgeId={selectedEdgeId}
        onClose={() => setSelectedEdgeId(null)}
      />
    </Card>
  );

  // Fullscreen renders at <body> so it escapes any transformed/overflow-clipped
  // layout ancestor and covers the whole viewport (sidebar + app bar included),
  // keeping the exit button and legend on top instead of behind the chrome.
  return fullscreen ? <Portal>{card}</Portal> : card;
};

export default RelationshipGraph;
